import * as React from "react";
import Partner from "../../../classes/Partner";
import { ContentType, HttpVerb, requestWithAuth } from "../../../utils/httpRequest";
import { _t } from "../../../utils/Translator";
import CollapseSection from "../CollapseSection/CollapseSection";
import ListItem from "../ListItem/ListItem";
import api from "../../api";
import AppContext from "../AppContext";
import { isMockMode, openOdooUrl, buildMockSectionRecord } from "../../../mock/mockInterceptor";
import Task from "../../../classes/Task";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatOdooDateTime, getOriginalMailDate } from "../../../utils/mailItemDate";

const FILTER_THRESHOLD = 5;

type SectionAbstractProps = {
  className?: string;
  records: any[];
  partner: Partner;
  canCreatePartner: boolean;

  // Odoo Record creation
  model: string;
  // endpoint used to create the record
  odooEndpointCreateRecord: string;
  // name of the key returned by Odoo containing the record ID
  odooRecordIdName: string;
  // Odoo action name used for the redirection
  odooRedirectAction: string;
  // Event when we click on the "+" button to create a new record
  // Can be intercepted to give additional values before creating the record
  // (e.g.: Search a project and add the project ID before creating a task)
  onClickCreate?: (callback: (values?, targetWindow?: Window | null) => void) => void;

  // Messages
  title: string;
  titleCount: string;
  msgNoPartner: string;
  msgNoPartnerNoAccess: string;
  msgNoRecord: string;
  msgLogEmail: string;
  getRecordDescription: (any) => string;
};

type SectionAbstractState = {
  records: any[];
  isCollapsed: boolean;
  filterQuery: string;
  isCreating: boolean;
  projectTasks?: Record<string, Task[]>;
};



/**
 * Section Component used to display the list of leads, tasks, tickets... Allow to create
 * the record, to log the email on the record or to hide them.
 */
class Section extends React.Component<SectionAbstractProps, SectionAbstractState> {
  private isUnmounted = false;

  constructor(props, context) {
    super(props, context);
    const isCollapsed = !props.records || !props.records.length;
    if (this.props.model === "project.task") {
      const projectTasks = this.groupTasksByProject(props.partner.tasks);
      this.state = {
        records: props.records,
        projectTasks,
        isCollapsed: isCollapsed,
        filterQuery: "",
        isCreating: false
      };
    } else {
      this.state = { records: this.props.records, isCollapsed: isCollapsed, filterQuery: "", isCreating: false };
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  private setStateIfMounted = (stateUpdate, callback?) => {
    if (!this.isUnmounted) {
      this.setState(stateUpdate, callback);
    }
  };

  private getItemSubjectAsync = (item: any): Promise<string> => {
    return new Promise(resolve => {
      const subject = item?.subject;
      if (typeof subject?.getAsync === "function") {
        subject.getAsync(result => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value || _t("Email"));
            return;
          }
          resolve(_t("Email"));
        });
      } else {
        if (typeof subject === "string") {
          resolve(subject || _t("Email"));
          return;
        }
        resolve(_t("Email"));
      }
    });
  };

  private groupTasksByProject(tasks: Task[]): Record<string, Task[]> {
    const groupedTasks: Record<string, Task[]> = {};

    tasks.forEach(task => {
      const projectName = task.projectName ? task.projectName : "unassigned";
      if (!groupedTasks[projectName]) {
        groupedTasks[projectName] = [];
      }
      groupedTasks[projectName].push(task);
    });

    return groupedTasks;
  }

  private onFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ filterQuery: event.target.value });
  };

  private clearFilter = () => {
    this.setState({ filterQuery: "" });
  };

  private matchesFilter = (record: any): boolean => {
    if (!this.state.filterQuery) return true;
    const query = this.state.filterQuery.toLowerCase();
    const name = (record.name || "").toLowerCase();
    const description = (this.props.getRecordDescription(record) || "").toLowerCase();
    return name.includes(query) || description.includes(query);
  };

  private getFilteredRecords = (): any[] => {
    if (!this.state.records || !this.state.filterQuery) return this.state.records;
    return this.state.records.filter(this.matchesFilter);
  };

  private onClickCreate = () => {
    if (this.state.isCreating) return;
    if (this.props.onClickCreate) {
      // Defer opening the target tab until the intercepted flow reaches its
      // final user gesture (for Tasks, selecting or creating a project).
      this.props.onClickCreate(this.createRecordWithWindow);
    } else {
      this.createRecordWithWindow();
    }
  };

  private createRecordWithWindow = (values?, targetWindow?: Window | null) => {
    // Opening synchronously from the final click avoids popup blockers. A
    // supplied window comes from an earlier step in the same creation flow.
    const recordWindow = targetWindow === undefined ? (isMockMode() ? null : window.open("", "_blank")) : targetWindow;
    this.createRecordRequest(values, recordWindow);
  };

  private createRecordRequest = (additionnalValues?, targetWindow?: Window | null) => {
    this.setStateIfMounted({ isCreating: true });
    Office.context.mailbox.item.body.getAsync(Office.CoercionType.Html, async result => {
      try {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          throw result.error || new Error("Could not read email body");
        }

        const item: any = Office.context.mailbox.item;
        const originalDate = getOriginalMailDate(item);
        // Remove the history and only log the most recent message.
        let message = result.value.split('<div id="x_appendonsend"></div>')[0];
        const subject = await this.getItemSubjectAsync(item);
        let context;

        if (this.props.model === "helpdesk.ticket") {
          additionnalValues = {
            ...additionnalValues,
            issue_name: subject
          };
        }

        if (this.props.model === "project.task") {
          additionnalValues = {
            ...additionnalValues,
            name: subject,
            description: message
          };
        }

        if (this.props.model === "crm.lead") {
          additionnalValues = {
            ...additionnalValues,
            name: subject
          };
        }

        if (this.props.model === "account.move") {
          context = {
            default_move_type: "out_invoice"
          };
        }

        const requestJson = Object.assign({
          args: [
            {
              partner_id: this.props.partner.id,
              ...additionnalValues
            }
          ],
          context: context
        });

        let responseText: string | null = null;
        try {
          const createResult = await requestWithAuth(
            HttpVerb.PATCH,
            api.baseURL + this.props.odooEndpointCreateRecord,
            ContentType.Json,
            requestJson
          ).promise;
          responseText = createResult.responseText;
        } catch (error) {
          this.context.showHttpErrorMessage(error);
          if (targetWindow && !targetWindow.closed) targetWindow.close();
          return;
        }

        const parsed = JSON.parse(responseText);
        if (parsed["error"] || !parsed.success) {
          this.context.showTopBarMessage();
          if (targetWindow && !targetWindow.closed) targetWindow.close();
          return;
        }
        const cids = this.context.getUserCompaniesString();
        const recordId = parsed.id || (parsed.data && parsed.data.id);
        const url = `${api.baseURL}/web#id=${recordId}&model=${this.props.model}&view_type=form${cids}`;

        // Open newly created record immediately (better UX and avoids popup blockers).
        openOdooUrl(url, targetWindow);

        // In demo mode no browser tab opens, so add the new record to the
        // visible list so the reviewer can see it was "created".
        if (isMockMode()) {
          const newRecord = buildMockSectionRecord(this.props.model, subject, additionnalValues);
          if (newRecord) {
            this.setStateIfMounted(prev => ({ records: [newRecord, ...(prev.records || [])] }));
          }
        }

        const senderEmail = item.sender?.emailAddress
          ?? item.from?.emailAddress
          ?? Office.context.mailbox.userProfile?.emailAddress
          ?? "";
        const msgHeader = senderEmail ? `<div>${_t("From : %(email)s", {
          email: senderEmail
        })}</div>` : "";
        const msgFooter = `<br/><div class="text-muted font-italic">${_t("Logged from Outlook Inbox")}</div>`;
        const body = result.value.split('<div id="x_appendonsend"></div>')[0]; // Remove the history and only log the most recent message.
        message = msgHeader + body + msgFooter;

        // Log the email in background, do not block opening.
        try {
          const logEmailRequest = requestWithAuth(
            HttpVerb.PATCH,
            api.baseURL + api.logSingleMail,
            ContentType.Json,
            {
              args: [
                {
                  model: this.props.model,
                  res_id: recordId,
                  body: message,
                  subject: subject,
                  partner_ids: [this.props.partner.id],
                  message_type: "email",
                  date: formatOdooDateTime(originalDate)
                }
              ]
            }
          );
          this.context.addRequestCanceller(logEmailRequest.cancel);
          await logEmailRequest.promise;
        } catch (error) {
          console.log(error);
          this.context.showHttpErrorMessage(error);
        }
      } catch (error) {
        this.context.showHttpErrorMessage(error);
        if (targetWindow && !targetWindow.closed) targetWindow.close();
      } finally {
        this.setStateIfMounted({ isCreating: false });
      }
    });
  };

  private renderFilterInput = () => {
    if (!this.state.records || this.state.records.length < FILTER_THRESHOLD) return null;

    return (
      <div className="section-filter">
        <FontAwesomeIcon icon={faSearch} className="section-filter-icon" />
        <input
          type="text"
          className="section-filter-input"
          placeholder={_t("Filter...")}
          value={this.state.filterQuery}
          onChange={this.onFilterChange}
          onClick={e => e.stopPropagation()}
        />
        {this.state.filterQuery && (
          <FontAwesomeIcon
            icon={faTimes}
            className="section-filter-clear"
            onClick={e => { e.stopPropagation(); this.clearFilter(); }}
          />
        )}
      </div>
    );
  };

  private getSection = () => {
    const filteredRecords = this.getFilteredRecords();
    const filterInput = this.renderFilterInput();

    if (this.state.records?.length > 0 && this.props.model === "project.task") {
      const uniqueProjects = Array.from(new Set(filteredRecords.map(task => task.projectName)));

      return (
        <div className="section-content">
          {filterInput}
          <div className="subtitle">Projects</div>
          {uniqueProjects.length === 0 && this.state.filterQuery ? (
            <div className="list-text">{_t("No matching tasks found.")}</div>
          ) : (
            uniqueProjects.map(projectName => {
              const projectTasks = filteredRecords.filter(task => task.projectName === projectName);

              return (
                <CollapseSection 
                  key={projectName}
                  title={projectName + " (" + projectTasks.length + ")"}
                  isCollapsed={true} 
                  hasAddButton={true}
                  isLoading={this.state.isCreating}
                  className="collapse-task-section"
                  onAddButtonClick={() => this.createRecordRequest({ project_id: projectTasks[0]?.projectId })}
                >
                  <>
                    {projectTasks.map(task => (
                      <ListItem
                        model={this.props.model}
                        res_id={task.id}
                        key={task.id}
                        title={task.name}
                        description={this.props.getRecordDescription(task)}
                        logTitle={_t(this.props.msgLogEmail)}
                        partner={this.props.partner}
                      />
                    ))}
                  </>
                </CollapseSection>
              );
            })
          )}
        </div>
      );
    } else if (this.state.records?.length > 0) {
      return (
        <div className="section-content">
          {filterInput}
          {filteredRecords.length === 0 && this.state.filterQuery ? (
            <div className="list-text">{_t("No matching records found.")}</div>
          ) : (
            filteredRecords.map(record => (
              <ListItem
                model={this.props.model}
                res_id={record.id}
                key={record.id}
                title={record.name || `Record #${record.id}`}
                description={this.props.getRecordDescription(record)}
                logTitle={_t(this.props.msgLogEmail)}
                partner={this.props.partner}
              />
            ))
          )}
        </div>
      );
    }
    return <div className="list-text">{_t(this.props.msgNoRecord)}</div>;
  };

  render() {
    const recordCount = this.state.records && this.state.records.length;
    const title = this.state.records
      ? _t(this.props.titleCount, { count: recordCount.toString() })
      : _t(this.props.title);

    return (
      <CollapseSection
        className={this.props.className}
        isCollapsed={this.state.isCollapsed}
        title={title}
        hasAddButton={this.props.partner.isAddedToDatabase()}
        isLoading={this.state.isCreating}
        onAddButtonClick={this.onClickCreate}
      >
        {this.getSection()}
      </CollapseSection>
    );
  }
}

Section.contextType = AppContext;
export default Section;
